import { Component, OnInit, ViewChildren, QueryList, ElementRef, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import { ProyectosService } from './../../services/proyectos.service';
import { Project, RecursoProject } from '../../../../../../interfaces/recursos/projects.entity';
import { RecursosService, ResourceWithValidations } from '../../../../../recursos/services/recursos.service';
import { showAlert, showConfirmAlert } from '../../../../../../utils/alert';
import { loadingAction } from '../../../../../../shared/state/actions/loading.action';
import { ApplicationStore } from '../../../../../../shared/state/store/index';
import { IdNameType } from '../../../../../../constants/types';
import { tipo_proyecto, tipo_financiacion } from '../../services/proyectos.service';
import { objectHasValues } from '../../../../../../utils/object';

type ResourceForm = Omit<RecursoProject, 'resource'> & {
	resource: ResourceWithValidations;
}

@Component({
	selector: 'app-allocate-resources',
	templateUrl: './allocate-resources.component.html',
	styleUrls: ['./allocate-resources.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class AllocateResourcesComponent implements OnInit, OnDestroy {
	@ViewChildren('resourceItem') set resourceItem(
		value: QueryList<ElementRef<HTMLDivElement>>,
	) {
		if (value && value.last && !this.initialLoad) value.last.nativeElement.scrollIntoView();
	}

	resourcesList: ResourceWithValidations[] = [];
	proyectResources: Project = {};
	formRecursos: FormGroup;
	projectId: string = '';
	frmProyecto: FormGroup = this.fb.group({
		nombre: ['', Validators.required],
		descripcion: ['', Validators.required],
		tipo_proyecto: ['', Validators.required],
		tipo_financiacion: ['', Validators.required],
	});
	project: Project | null = null;
	initialLoad: boolean = true;
	list_tipos_proyecto: IdNameType[] = [];
	list_tipo_financiacion: IdNameType[] = [];
	dialogSubscription = new Subscription();
	hasDeliveries: boolean = false;

	get resources(): FormArray {
		return this.formRecursos.controls['recursos'] as FormArray;
	}

	constructor(
		private activateRoute: ActivatedRoute,
		private proyectsService: ProyectosService,
		private fb: FormBuilder,
		private router: Router,
		private recursosService: RecursosService,
		private store: Store<ApplicationStore>,
		private cdr: ChangeDetectorRef
	) {
		this.formRecursos = this.fb.group({
			recursos: this.fb.array([]),
		});
	}

	ngOnInit(): void {
		this.list_tipos_proyecto = tipo_proyecto;
		this.list_tipo_financiacion = tipo_financiacion;

		this.store.dispatch(loadingAction({ loading: true }));

		const { params } = this.activateRoute.snapshot;
		const { id } = params;
		this.projectId = id;

		forkJoin({
			project: this.projectId ? this.proyectsService.getProject(this.projectId) : of(null),
			resources: this.recursosService.getResources(this.projectId),
			projectResources: this.projectId ? this.proyectsService.getResources(this.projectId, 'proyecto') : of([])
		}).subscribe(({ project, resources, projectResources }) => {
			this.resourcesList = resources;
			this.project = project;
			// this.hasDeliveries = project?.hasDeliveries ?? false;

			// Data edition
			if (this.project) {
				this.frmProyecto.reset({
					nombre: this.project.name,
					descripcion: this.project.description,
					tipo_proyecto: this.project.type === 1 ? 'convenio' : 'contrato',
					tipo_financiacion: this.project.funding_type === 1 ? 'recursosPropios' : 'regalias',
				});
			}

			if (projectResources.length > 0) {
				for (const projectResource of projectResources) {
					this.addResource(
						projectResource.id,
						projectResource.resource?.id,
						projectResource.observations,
						projectResource.quantity,
						projectResource.apply_serial
					);
				}
			} else {
				this.addResource();
			}
			if (this.hasDeliveries) {
				this.frmProyecto.get('nombre')?.disable();
				this.frmProyecto.get('descripcion')?.disable();
				this.frmProyecto.get('tipo_financiacion')?.disable();
				this.frmProyecto.get('tipo_proyecto')?.disable();
			}
			this.store.dispatch(loadingAction({ loading: false }));
		});

	}

	getFormGroupByItem(index: number): FormGroup {
		return this.resources.controls.at(index) as FormGroup;
	}

	addResourceButton() {
		this.initialLoad = false;
		this.addResource();
	}

	addResource(id = '', id_resource = '', observations = '', quantity = 0, apply_serial = false) {
		const resource = this.fb.group({
			id: [id],
			resource: new FormControl({
				value: this.resourcesList.find(r => r.id === id_resource),
				disabled: this.hasDeliveries
			}, {
				validators: [Validators.required],
			}),
			observations: new FormControl({
				value: observations,
				disabled: this.hasDeliveries
			}),
			quantity: new FormControl({
				value: quantity,
				disabled: this.hasDeliveries
			}, {
				validators: [Validators.required],
			}),
			apply_serial: new FormControl({
				value: apply_serial,
				disabled: this.hasDeliveries
			}),
		});

		this.resources.push(resource);
	}

	saveResources() {
		if (this.frmProyecto.valid && this.formRecursos.valid) {
			this.store.dispatch(loadingAction({ loading: true }));
			const projectValue = this.frmProyecto.getRawValue();

			const payload: Project = {
				name: projectValue.nombre,
				description: projectValue.descripcion,
				type: projectValue.tipo_proyecto,
				funding_type: projectValue.tipo_financiacion,
			};

			if (!this.project) {
				this.proyectsService.create(payload).subscribe(({ id }) => {
					this.saveResourcesData(id);
				});
			} else {
				this.proyectsService.update({
					id: this.projectId,
					...payload,
				}).subscribe(() => {
					// Editar
					this.saveResourcesData(this.projectId);
				});
			}
		}
	}

	saveResourcesData(projectId: string) {
		const resourcesProyect = this.formRecursos.getRawValue().recursos.map((recurso: ResourceForm) => ({
			id: recurso.id ? recurso.id : undefined,
			quantity: recurso.quantity,
			observations: recurso.observations,
			apply_serial: recurso.apply_serial,
			id_resource: recurso.resource.id,
		}));

		this.proyectsService.associateResources(projectId, resourcesProyect).subscribe(() => {
			this.store.dispatch(loadingAction({ loading: false }));
			showAlert('Proyecto guardado', () => {
				this.router.navigate(['/recursos/proyectos']);
			});
		});
	}

	deleteResource(index: number) {
		const control = this.resources.at(index);
		if (objectHasValues(control.value)) {
			showConfirmAlert({
				message: '¿Seguro de eliminar el recurso?',
				callBack: () => {
					this.resources.removeAt(index);
				}
			});
		} else {
			this.resources.removeAt(index);
		}
	}

	ngOnDestroy(): void {
		this.dialogSubscription.unsubscribe();
	}
}
